import { Injectable, inject } from '@angular/core';
import { SessionService } from './session.service';
import { jwtDecode } from 'jwt-decode';
import { v4 as uuidv4, v5 as uuidv5 } from 'uuid';
import { Device } from '@capacitor/device';

import {
  APP_OPEN_ROUTE,
  DAILY_STORY_ROUTE,
  DEVICE_PUSH_ROUTE,
  GET_ANONYMOUS_TOKEN_ROUTE,
  ImageStyleName,
  NULL_UUID,
  REPORTS_ROUTE,
  PUSH_INTERACTION_ROUTE,
  SELECTED_PROFILE_ID,
  STORIES_ROUTE,
  STORIES_IMPORT_SHARED_STORY_ROUTE,
  STORY_LENGTHS,
  UPGRADE_USER_ROUTE,
  USERS_ROUTE,
  VOICE_CLONE_NARRATION_ACCESS_ROUTE,
  VOICE_CLONE_TRIAL_START_ROUTE,
  VOICE_CLONE_TRIAL_OPT_OUT_ROUTE,
  getStoreesNamespace,
  NARRATIVE_STYLE_CHOOSE_YOUR_OWN,
  NARRATIVE_STYLE_STANDARD,
  CYOA_SEGMENT_COUNT_MAX,
  CYOA_SEGMENT_COUNT_MIN,
} from '@storees/shared-constants';
import { Constants } from '../utils/Constants';
import urlJoin from 'url-join';
import { CapacitorHttp, HttpOptions } from '@capacitor/core';
import { NarratorVoice, StoryElementInputKey, components } from '@storees/api-types';
import { environment } from '@storees/environments';
import { AlertController } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { StoryCreationStateService } from './story-creation-state.service';
import { CyoaLocalBranchCacheService } from './cyoa-local-branch-cache.service';
import { TrophiesService, TrophyUnlockNotification, FixedProgressTrophyType } from './trophies-service';
import { StoreesSubscriptionService } from './storees-subscription-service';
import { DexieDbService } from './dexie/dexie-db-service';
import { logger } from '../utils/logger';
import { promiseWithTimeout, STOREES_BACKEND_HTTP_TIMEOUT_MS } from '../utils/async-timeout';

const { apiBaseUrl, storeesBackendApiKey, defaultVoice } = environment;
type AnonymousTokenResponse = components['schemas']['AnonymousTokenResponse'];
type StoryResponse = components['schemas']['StoryResponse'];

interface JwtPayload {
  sub: string;
  exp: number;
  iat: number;
  aud: string;
  iss: string;
  name: string;
}

export interface CurrentUserResponse {
  id: string;
  email: string | null;
  type: string;
  voiceCloneAccess: VoiceCloneTrialState;
}

export type VoiceCloneTrialState = {
  allowed: boolean;
  source: 'bronze' | 'weekly_free_premium' | 'trial' | null;
  reason?:
    | 'trial_not_started'
    | 'trial_narrations_exhausted'
    | 'trial_expired'
    | 'trial_opted_out'
    | 'no_access'
    | 'billing_unavailable';
  trialStartedAt: string | null;
  trialOptedOut: boolean;
  narrationsUsed: number;
  narrationsMax: number;
  trialDaysTotal: number;
  trialDaysRemaining: number | null;
  trialActive: boolean;
  weeklyFreePremiumExpiresAt: string | null;
};

export interface StoryCreationResult {
  storyId: string;
  bucketEndpoint: string;
  bucketName: string;
  createdAt: string;
  /**
   * CYOA continuation only — parent story id the backend accepted (echoed
   * from the request). Lets the client confirm the parent link the server is
   * using before the worker finishes persisting the new segment.
   */
  previousStoryId?: string | null;
  /** CYOA only — 1-based segment index along the branch. */
  cyoaSegmentIndex?: number | null;
}

/**
 * Thrown by `createStory` so callers can inspect HTTP status / server error
 * text and decide whether to retry, restart, or surface a specific error
 * (e.g., a CYOA continuation referencing a parent the backend no longer has).
 */
export class StoryCreationError extends Error {
  public readonly status: number | null;
  public readonly serverError: string | null;
  public readonly wasContinuation: boolean;

  constructor(params: {
    message: string;
    status: number | null;
    serverError: string | null;
    wasContinuation: boolean;
  }) {
    super(params.message);
    this.name = 'StoryCreationError';
    this.status = params.status;
    this.serverError = params.serverError;
    this.wasContinuation = params.wasContinuation;
  }

  /**
   * True for continuation POST failures that are surfaced as HTTP 400 with
   * wording about `previousStoryId` / choice / continuation context. The
   * client treats these as a distinct UX path (retry vs start over). In
   * mixed worker deployments the same payload may succeed after retry.
   */
  public get isInvalidContinuation(): boolean {
    if (!this.wasContinuation || this.status !== 400) {
      return false;
    }
    const text = (this.serverError ?? '').toLowerCase();
    return (
      text.includes('previousstoryid') ||
      text.includes('selectedchoiceindex') ||
      text.includes('continuation context')
    );
  }
}

/**
 * When multiple API/worker versions are live, an older node may reject a valid
 * CYOA continuation with this message while a newer node accepts it. Keep
 * continuation params so the user can retry the same request.
 */
function isLikelyMixedWorkerRecentStoryRejection(
  serverError: string | null | undefined,
): boolean {
  const text = (serverError ?? '').toLowerCase();
  return (
    text.includes('previousstoryid') &&
    text.includes('recent') &&
    text.includes('profile')
  );
}

export interface DevicePushRegistrationPayload {
  /** Stable internal `User.id` from `GET /v1/users/me` — not `UserIdentity.providerUserId` (Ory uses that for Qonversion separately). */
  userId: string;
  installationId: string;
  token: string;
  platform: 'ios' | 'android';
}

export interface DailyStoryResponse {
  id: string;
  campaignDate: string;
  eventName: string;
  tagline?: string;
  elements: components['schemas']['StoryElementInput'][];
  expiresAt: string;
}

export interface PushInteractionPayload {
  campaignType: 'day_story';
  campaignDate?: string;
  action: 'tap' | 'app_activity';
  /** Present for app_activity: how the client detected activity (foreground, input, tab focus). */
  source?: 'resume' | 'gesture' | 'visibility';
}

@Injectable({
  providedIn: 'root',
})
export class StoreesBackendService {
  private translate = inject(TranslateService);
  private alertCtrl = inject(AlertController);
  private sessionService = inject(SessionService);
  private dexieDb = inject(DexieDbService);
  private storyCreationStateService = inject(StoryCreationStateService);
  private cyoaLocalBranchCache = inject(CyoaLocalBranchCacheService);
  private trophiesService = inject(TrophiesService);
  private storeesSubscriptionService = inject(StoreesSubscriptionService);

  private readonly PENDING_TROPHY_UNLOCKS_KEY = 'pending_trophy_unlocks';
  private readonly DEVICE_UUID_KEY = 'device_uuid';
  private readonly STABLE_ANONYMOUS_ID_KEY = 'stable_anonymous_id';

  /** Coalesces concurrent anonymous-token fetches (startup can trigger several JWT paths). */
  private anonymousTokenInFlight: Promise<string> | null = null;

  /**
   * Snapshot of the last `POST /v1/stories` payload this client built. Cached
   * so retry handlers can resubmit the same request — including the
   * `previousStoryId` / `selectedChoiceIndex` that `clearContinuationParams()`
   * removes from {@link StoryCreationStateService} after a successful POST.
   *
   * Without this, "Try Again" after a worker error mid-CYOA-segment would
   * rebuild the payload from the (now empty) continuation state and start a
   * brand-new ROOT story instead of regenerating the failed segment.
   *
   * Cleared explicitly by {@link clearLastSubmittedStoryRequest} on
   * "Start a New Story" / fresh navigation flows.
   */
  private lastSubmittedStoryRequest: Record<string, unknown> | null = null;

  /**
   * @returns Whether {@link createStory} has a cached prior request payload
   * that callers can resubmit via `{ resubmitLastRequest: true }`.
   */
  public hasResubmittableStoryRequest(): boolean {
    return this.lastSubmittedStoryRequest !== null;
  }

  /**
   * Drop the cached prior request so the next {@link createStory} call must
   * rebuild from current {@link StoryCreationStateService} selections. Called
   * when the user explicitly opts into a fresh root story (e.g. tapping
   * "Start a New Story" on a CYOA continuation error).
   */
  public clearLastSubmittedStoryRequest(): void {
    this.lastSubmittedStoryRequest = null;
  }

  private isRealProfileId(profileId: unknown): profileId is string {
    return (
      typeof profileId === 'string' &&
      profileId.trim().length > 0 &&
      profileId !== NULL_UUID
    );
  }

  private async shouldRetryStoryCreateWithVoiceCloneFallback(
    status: number | null,
    responseData: unknown,
    requestData: Record<string, unknown>,
    isContinuation: boolean,
  ): Promise<boolean> {
    if (status !== 403) {
      return false;
    }

    const requestedVoice =
      typeof requestData['voice'] === 'string'
        ? requestData['voice'].toLowerCase()
        : '';
    if (requestedVoice !== NarratorVoice.voice_clone) {
      return false;
    }

    const reason =
      typeof responseData === 'object' &&
      responseData !== null &&
      'reason' in responseData &&
      typeof (responseData as { reason?: unknown }).reason === 'string'
        ? ((responseData as { reason?: string }).reason ?? '').toLowerCase()
        : '';

    const matchedAccessReason =
      reason === 'trial_opted_out' ||
      reason === 'no_access' ||
      reason === 'billing_unavailable';

    const serverMessage =
      typeof responseData === 'object' && responseData !== null
        ? String(
            (responseData as { message?: unknown; error?: unknown }).message ??
              (responseData as { message?: unknown; error?: unknown }).error ??
              '',
          ).toLowerCase()
        : '';

    const matchedServerMessage =
      serverMessage.includes('no active voice clone narration access');
    if (!matchedAccessReason && !matchedServerMessage) {
      return false;
    }

    if (isContinuation) {
      return true;
    }

    // New stories: if the user has a locally-active Bronze subscription but
    // the backend still denies voice-clone access (subscription webhook lag),
    // do NOT silently fall back to Heart — the user deliberately chose their
    // AI voice. Return false so the 403 propagates as an error; the caller
    // will surface a user-friendly "activating subscription" message.
    try {
      const hasLocalBronzeLikeAccess =
        this.storeesSubscriptionService.isSubscribed;
      if (hasLocalBronzeLikeAccess) {
        logger.warn(
          'StoreesBackendService: not retrying with Heart fallback — user is subscribed locally but backend has stale voice-clone access state',
          {
            responseReason: reason || null,
            responseMessage: serverMessage || null,
          },
        );
        return false;
      }
    } catch (error) {
      logger.warn(
        'StoreesBackendService: failed checking local entitlement mismatch for non-continuation voice-clone retry',
        error,
      );
    }

    return false;
  }

  private async buildStoryRequestWithHeartFallback(
    requestData: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    const fallbackRequest: Record<string, unknown> = {
      ...requestData,
      voice: NarratorVoice.af_heart,
    };
    const heartNarratorId = await this.resolveAfHeartNarratorId();
    if (heartNarratorId) {
      fallbackRequest['narratorId'] = heartNarratorId;
    } else {
      delete fallbackRequest['narratorId'];
    }
    return fallbackRequest;
  }

  /**
   * Returns true when:
   *   1. The failed request used voice_clone.
   *   2. The backend reason is a voice-clone access denial.
   *   3. Qonversion considers the user subscribed (optimistic override).
   *
   * In that case the caller should wait a few seconds and retry the exact same
   * request — the subscription webhook will have propagated by then.
   */
  private async canRetryStoryAfterSubscriptionLag(
    responseData: unknown,
    requestData: Record<string, unknown>,
  ): Promise<boolean> {
    const requestedVoice =
      typeof requestData['voice'] === 'string'
        ? requestData['voice'].toLowerCase()
        : '';
    if (requestedVoice !== NarratorVoice.voice_clone) {
      return false;
    }

    const reason =
      typeof responseData === 'object' &&
      responseData !== null &&
      'reason' in responseData &&
      typeof (responseData as { reason?: unknown }).reason === 'string'
        ? ((responseData as { reason?: string }).reason ?? '').toLowerCase()
        : '';

    if (
      reason !== 'trial_opted_out' &&
      reason !== 'no_access' &&
      reason !== 'billing_unavailable'
    ) {
      return false;
    }

    try {
      return this.storeesSubscriptionService.isSubscribed;
    } catch {
      return false;
    }
  }

  private async invalidateVoiceCloneTrialCacheAfterSubscriptionSync(): Promise<void> {
    try {
      await this.storeesSubscriptionService.checkEntitlements({ force: true });
    } catch {
      // best effort — story retry still uses voice_clone request
    }
  }

  private async resolveAfHeartNarratorId(): Promise<string | null> {
    try {
      const narrators = await this.dexieDb.getNarrators();
      const heartNarrator = narrators.find(
        (narrator) => narrator.voice === NarratorVoice.af_heart,
      );
      return heartNarrator?.id ?? null;
    } catch (error) {
      logger.warn(
        'StoreesBackendService: failed resolving Heart narrator id for fallback request',
        error,
      );
      return null;
    }
  }

  private async resolveSelectedProfileIdForStoryCreation(): Promise<string | null> {
    const selectedProfileId = await this.sessionService.getPersistent(
      SELECTED_PROFILE_ID,
    );
    if (this.isRealProfileId(selectedProfileId)) {
      return selectedProfileId;
    }

    const selectedProfile = await this.sessionService.getPersistent(
      'selectedProfile',
    );
    const selectedProfileObject =
      selectedProfile && typeof selectedProfile === 'object'
        ? (selectedProfile as { id?: unknown })
        : null;
    if (this.isRealProfileId(selectedProfileObject?.id)) {
      await this.sessionService.setPersistent(
        SELECTED_PROFILE_ID,
        selectedProfileObject.id,
      );
      return selectedProfileObject.id;
    }

    const localProfiles = await this.dexieDb.getProfiles();
    const fallbackProfile = localProfiles.find((profile) =>
      this.isRealProfileId(profile?.id),
    );
    if (!this.isRealProfileId(fallbackProfile?.id)) {
      return null;
    }

    await this.sessionService.setPersistent(SELECTED_PROFILE_ID, fallbackProfile.id);
    await this.sessionService.setPersistent('selectedProfile', fallbackProfile);
    logger.warn(
      'StoreesBackendService: repaired missing selected profile id from Dexie',
      { profileIdSuffix: fallbackProfile.id.slice(-12) },
    );
    return fallbackProfile.id;
  }

  public async createStory(
    jwtToken: string,
    callOptions?: { resubmitLastRequest?: boolean },
  ): Promise<StoryCreationResult> {
    const narratorVoiceParams = this.storyCreationStateService.getNarratorVoiceParams();

    let data: Record<string, unknown>;
    let continuation: {
      previousStoryId?: string;
      selectedChoiceIndex?: number;
    };
    let isContinuation: boolean;
    let elements: components['schemas']['StoryElementInput'][] | undefined;

    if (callOptions?.resubmitLastRequest && this.lastSubmittedStoryRequest) {
      // Reissue the exact previous payload. Critical for CYOA: after a
      // successful POST the continuation params are cleared (so the user can
      // start a fresh root story), but if the worker subsequently fails we
      // still need to retry that same continuation rather than silently
      // creating a new root story under the user's "Try Again" tap.
      data = { ...this.lastSubmittedStoryRequest };
      continuation = {
        previousStoryId: data['previousStoryId'] as string | undefined,
        selectedChoiceIndex: data['selectedChoiceIndex'] as number | undefined,
      };
      isContinuation = continuation.previousStoryId != null;
      elements = data['elements'] as
        | components['schemas']['StoryElementInput'][]
        | undefined;
      logger.warn(
        'StoreesBackendService: resubmitting last story request',
        JSON.stringify({
          isContinuation,
          previousStoryId: continuation.previousStoryId ?? null,
          selectedChoiceIndex: continuation.selectedChoiceIndex ?? null,
          profileId: data['profileId'],
        }),
      );
    } else {
      const profileId = await this.resolveSelectedProfileIdForStoryCreation();
      if (!profileId) {
        throw new Error('Please select or create a profile before continuing the story.');
      }
      continuation =
        this.storyCreationStateService.getContinuationPayload() ?? {};
      isContinuation = continuation?.previousStoryId != null;
      // CYOA continuations MUST NOT include `elements` — the backend
      // authoritatively rebuilds them from the root segment of the
      // persisted chain so narration stays consistent with the original
      // creation parameters. Sending elements on a continuation only
      // wastes bandwidth (the backend ignores them) and risks confusing
      // logs. For brand-new stories we always send the elements built
      // from the user's selections.
      elements = isContinuation
        ? undefined
        : this.storyCreationStateService.buildElements();

      // Continuations always require choose_your_own (backend rejects
      // previousStoryId otherwise). Share-import playback never runs the
      // narrative-style modal, so creation state may still say "standard".
      const narrativeStyle =
        isContinuation ||
        this.storyCreationStateService.isChooseYourOwnNarrative()
          ? NARRATIVE_STYLE_CHOOSE_YOUR_OWN
          : NARRATIVE_STYLE_STANDARD;

      data = {
        voice: narratorVoiceParams.name || defaultVoice,
        profileId,
        narratorId: narratorVoiceParams.narratorId,
        imageStyle:
          this.storyCreationStateService.getImageStyleParams().name ||
          ImageStyleName.CARTOON_3D,
        narrativeStyle,
        ...continuation,
      };
      if (
        narrativeStyle === NARRATIVE_STYLE_CHOOSE_YOUR_OWN &&
        !isContinuation
      ) {
        const segParams =
          this.storyCreationStateService.getCyoaSegmentCountParams();
        const raw = parseInt(
          String(segParams.name ?? segParams.description ?? CYOA_SEGMENT_COUNT_MAX),
          10,
        );
        const cyoaSegmentCount = Number.isFinite(raw)
          ? Math.min(
              CYOA_SEGMENT_COUNT_MAX,
              Math.max(CYOA_SEGMENT_COUNT_MIN, raw),
            )
          : CYOA_SEGMENT_COUNT_MAX;
        data['cyoaSegmentCount'] = cyoaSegmentCount;
      }
      if (elements && elements.length > 0) {
        data['elements'] = elements;
      }
    }

    if (!this.isRealProfileId(data['profileId'])) {
      const repairedProfileId = await this.resolveSelectedProfileIdForStoryCreation();
      if (!repairedProfileId) {
        throw new Error('Please select or create a profile before continuing the story.');
      }
      data['profileId'] = repairedProfileId;
      logger.warn(
        'StoreesBackendService: repaired missing profileId on story request payload',
        {
          profileIdSuffix: repairedProfileId.slice(-12),
          isResubmit: callOptions?.resubmitLastRequest === true,
        },
      );
    }

    // Cache the payload before POSTing so a later retry — even one that
    // happens after `clearContinuationParams()` runs in the success branch
    // below — can resubmit the same request. Snapshot to defeat aliasing
    // through `data` (we do not mutate the wire payload after this point).
    this.lastSubmittedStoryRequest = JSON.parse(JSON.stringify(data));

    const themeElement = (elements ?? []).find((el) => el.key === StoryElementInputKey.Theme);
    logger.info(
      'StoreesBackendService: story generation request (wire payload + Theme/FeaturedObservance)',
      JSON.stringify({
        finalPromptClientPreview: this.storyCreationStateService.generateFinalPrompt(),
        featuredObservanceThemeElement: themeElement
          ? {
              key: themeElement.key,
              name: themeElement.name,
              description: themeElement.description,
            }
          : null,
        storyRequest: data,
      }),
    );

    const requestOptionsBase = {
      url: this.getStoreesBackendUrl(STORIES_ROUTE),
      method: 'POST',
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };

    let requestData = data;
    let response = await CapacitorHttp.request({
      ...requestOptionsBase,
      data: requestData,
    });

    if (
      (!response || response.status < 200 || response.status >= 300) &&
      (await this.shouldRetryStoryCreateWithVoiceCloneFallback(
        response?.status ?? null,
        response?.data,
        requestData,
        isContinuation,
      ))
    ) {
      const previousReason =
        typeof response?.data?.reason === 'string' ? response.data.reason : null;
      logger.warn(
        'StoreesBackendService: retrying story generation with Heart fallback after voice-clone narration denial',
        {
          previousStatus: response?.status ?? null,
          previousReason,
          requestedVoice: requestData['voice'] as string | undefined,
          narratorId: (requestData['narratorId'] as string | undefined) ?? null,
          wasContinuation: isContinuation,
        },
      );

      requestData = await this.buildStoryRequestWithHeartFallback(requestData);
      this.lastSubmittedStoryRequest = JSON.parse(JSON.stringify(requestData));
      response = await CapacitorHttp.request({
        ...requestOptionsBase,
        data: requestData,
      });
    }

    // Subscription webhook lag: the user just subscribed but the backend
    // Qonversion lookup hasn't converged yet (trial_opted_out / no_access).
    // Retry a few times with short delays — the post-purchase identify() call
    // usually lets the backend resolve Bronze within a couple of seconds.
    if (response?.status === 403 && !isContinuation) {
      const lagRetry = await this.canRetryStoryAfterSubscriptionLag(
        response.data,
        requestData,
      );
      if (lagRetry) {
        await this.invalidateVoiceCloneTrialCacheAfterSubscriptionSync();
        const retryDelaysMs = [0, 1_000, 3_000];
        for (const delayMs of retryDelaysMs) {
          if (delayMs > 0) {
            logger.warn(
              `StoreesBackendService: subscription lag detected; retrying after ${delayMs / 1000} s`,
              { requestedVoice: requestData['voice'] as string | undefined },
            );
            await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
          }
          response = await CapacitorHttp.request({
            ...requestOptionsBase,
            data: requestData,
          });
          this.lastSubmittedStoryRequest = JSON.parse(JSON.stringify(requestData));
          if (response?.status !== 403) {
            break;
          }
          const stillLag = await this.canRetryStoryAfterSubscriptionLag(
            response.data,
            requestData,
          );
          if (!stillLag) {
            break;
          }
        }
      }
    }

    // Check for HTTP errors
    if (!response || response.status < 200 || response.status >= 300) {
      let serverError = response?.data?.message || response?.data?.error || 'Story creation failed';
      const status = response?.status ?? null;

      // When the backend denies a voice-clone story with trial_opted_out/no_access
      // but the user is locally subscribed (Qonversion webhook lag), surface a
      // retry-friendly message rather than confusing internal error text.
      const deniedReason = typeof response?.data?.reason === 'string'
        ? (response.data.reason as string).toLowerCase()
        : '';
      const wasVoiceCloneRequest = (requestData['voice'] as string | undefined) === NarratorVoice.voice_clone;
      if (
        status === 403 &&
        wasVoiceCloneRequest &&
        !isContinuation &&
        (deniedReason === 'trial_opted_out' || deniedReason === 'no_access' || deniedReason === 'billing_unavailable')
      ) {
        try {
          if (this.storeesSubscriptionService.isSubscribed) {
            serverError = 'Your subscription is still activating. Please try again in a moment.';
          }
        } catch {
          // keep original error message
        }
      }

      logger.error(
        'StoreesBackendService: story generation request failed',
        JSON.stringify({
          status,
          errorMessage: serverError,
          reason: response?.data?.reason ?? null,
          source: response?.data?.source ?? null,
          requestedVoice: requestData['voice'] as string | undefined,
          narratorId: (requestData['narratorId'] as string | undefined) ?? null,
          profileId: requestData['profileId'] as string | undefined,
          wasContinuation: isContinuation,
        })
      );

      // Drop continuation params on most continuation 400s so the client does
      // not loop on a permanently invalid payload. Exception: the "recent
      // story for this profile" response may come from an outdated worker
      // while another node would accept the same continuation — keep params
      // so the loading screen can offer Retry.
      if (
        isContinuation &&
        status === 400 &&
        !isLikelyMixedWorkerRecentStoryRejection(serverError)
      ) {
        logger.warn(
          'StoreesBackendService: clearing continuation params after 400 (non-retryable continuation error)',
        );
        this.storyCreationStateService.clearContinuationParams();
      } else if (
        isContinuation &&
        status === 400 &&
        isLikelyMixedWorkerRecentStoryRejection(serverError)
      ) {
        logger.warn(
          'StoreesBackendService: keeping continuation params after 400 (recent-story message; user may retry)',
        );
      }

      throw new StoryCreationError({
        message: `Failed to create story: ${serverError} (HTTP ${status ?? 'unknown'})`,
        status,
        serverError,
        wasContinuation: isContinuation,
      });
    }

    if (response) {
      const storyResponse = response.data as StoryResponse;
      const continuationOnSuccess =
        this.storyCreationStateService.getContinuationPayload();
      if (
        continuationOnSuccess.previousStoryId != null &&
        continuationOnSuccess.selectedChoiceIndex != null &&
        storyResponse.storyId
      ) {
        this.cyoaLocalBranchCache.record(
          continuationOnSuccess.previousStoryId,
          continuationOnSuccess.selectedChoiceIndex,
          storyResponse.storyId,
        );
      }
      this.storyCreationStateService.clearContinuationParams();

      logger.debug('Backend Service - Story created, tracking trophies...');

      // Capture story creation parameters
      const narratorVoiceId =
        (requestData['voice'] as string | undefined) || narratorVoiceParams.name;
      // Use description field which now stores the display name
      const narratorVoiceDisplayName =
        narratorVoiceId === NarratorVoice.af_heart
          ? 'Heart'
          : narratorVoiceParams.description || narratorVoiceId;

      // Look up length display name from STORY_LENGTHS
      const lengthId = this.storyCreationStateService.getLengthParams().name;
      const lengthEntry = STORY_LENGTHS.find(length => length.id === lengthId);
      const lengthDisplayName = lengthEntry?.name || lengthId;

      const storyParams = {
        genre: this.storyCreationStateService.getGenreParams().name,
        setting: this.storyCreationStateService.getSettingParams().name,
        theme: this.storyCreationStateService.getThemeParams().name,
        plot: this.storyCreationStateService.getPlotParams().name,
        conflict: this.storyCreationStateService.getConflictParams().name,
        tone: this.storyCreationStateService.getToneParams().name,
        length: lengthId,
        lengthDisplayName: lengthDisplayName,
        imageStyle: this.storyCreationStateService.getImageStyleParams().name,
        narratorVoice: narratorVoiceId,
        narratorVoiceDisplayName: narratorVoiceDisplayName,
        characterRole: this.storyCreationStateService.getSelectedRole(),
        characterType: this.storyCreationStateService.getSelectedType(),
      };

      logger.debug('Backend Service - 🎮 Story creation params for trophy tracking:', storyParams);
      logger.debug('Backend Service - ========================================');

      // Record story creation (tracks MilestoneStoriesCreate and today's story count)
      // NOTE: incrementTodayStoryCount() now IMMEDIATELY increments streaks when thresholds are crossed
      // and tracks which pools received recharges
      logger.debug('Backend Service - 📝 STEP 1: Calling recordStoryCreation...');
      const { trophyUnlocks: recordUnlocks, rechargedPools, thresholdsAccomplishedAtMax } = await this.trophiesService.recordStoryCreation();
      logger.debug('Backend Service - ✅ recordStoryCreation returned:', recordUnlocks.length, 'unlocks,', rechargedPools.length, 'recharged pools, and', thresholdsAccomplishedAtMax.length, 'accomplished at max');
      if (recordUnlocks.length > 0) {
        logger.debug('Backend Service - Record unlocks:', recordUnlocks);
      }
      if (rechargedPools.length > 0) {
        logger.debug('Backend Service - 🔋 Pools that received recharges:', rechargedPools);
      }
      if (thresholdsAccomplishedAtMax.length > 0) {
        logger.debug('Backend Service - 💎 Thresholds accomplished at max (visual feedback):', thresholdsAccomplishedAtMax);
      }
      logger.debug('Backend Service - 💾 Modal data stored in Dexie (not session storage) for cross-device sync');

      // Track trophies for story creation (tracks story-specific trophies)
      logger.debug('Backend Service - 📝 STEP 2: Calling trackStoryCreation...');
      const trophyUnlocks = await this.trophiesService.trackStoryCreation(storyParams);
      logger.debug('Backend Service - ✅ trackStoryCreation returned:', trophyUnlocks.length, 'unlocks');
      if (trophyUnlocks.length > 0) {
        logger.debug('Backend Service - Track unlocks:', trophyUnlocks);
      }

      // Check for streak milestone trophies (streaks are updated immediately in recordStoryCreation)
      logger.debug('Backend Service - 📝 STEP 3: Calling checkStreakMilestoneTrophies...');
      const streakMilestoneUnlocks = await this.trophiesService.checkStreakMilestoneTrophies();
      logger.debug('Backend Service - ✅ checkStreakMilestoneTrophies returned:', streakMilestoneUnlocks.length, 'unlocks');
      if (streakMilestoneUnlocks.length > 0) {
        logger.debug('Backend Service - 🏆 STREAK MILESTONE UNLOCKS:', streakMilestoneUnlocks);
      } else {
        logger.debug('Backend Service - ❌ NO STREAK MILESTONE UNLOCKS');
      }

      // Merge all trophy unlocks together
      logger.debug('Backend Service - 📦 STEP 4: Merging all trophy unlocks...');
      logger.debug('Backend Service - Before merge - trophyUnlocks.length:', trophyUnlocks.length);

      if (recordUnlocks.length > 0) {
        trophyUnlocks.push(...recordUnlocks);
        logger.debug('Backend Service - Added', recordUnlocks.length, 'record unlocks. New length:', trophyUnlocks.length);
      }
      if (streakMilestoneUnlocks.length > 0) {
        trophyUnlocks.push(...streakMilestoneUnlocks);
        logger.debug('Backend Service - 🎉 Added', streakMilestoneUnlocks.length, 'streak milestone unlocks. New length:', trophyUnlocks.length);
      }

      logger.debug('Backend Service - 📊 FINAL TROPHY COUNT:', trophyUnlocks.length);
      if (trophyUnlocks.length > 0) {
        logger.debug('Backend Service - 🎁 ALL COMBINED TROPHY UNLOCKS:', trophyUnlocks);
      } else {
        logger.debug('Backend Service - ⚠️ NO TROPHIES TO UNLOCK');
      }
      logger.debug('Backend Service - ========================================');

      // Check if user added additional characters (Character Creator trophy)
      const selectedAvatars = this.storyCreationStateService.getSelectedAvatars();
      const hasAdditionalCharacters = selectedAvatars.some(avatar => avatar === true);
      logger.debug('Backend Service - Selected avatars:', selectedAvatars, 'has additional characters:', hasAdditionalCharacters);

      if (hasAdditionalCharacters && !(await this.trophiesService.isTrophyUnlocked(FixedProgressTrophyType.FixedCharacter))) {
        logger.debug('Backend Service - Additional characters detected, adding Character Creator trophy');
        await this.trophiesService.incrementTrophy(FixedProgressTrophyType.FixedCharacter);
        trophyUnlocks.push({
          trophyType: FixedProgressTrophyType.FixedCharacter,
          isNewlyUnlocked: true
        });
      }

      // Store trophy unlocks for LoadingScreenComponent to display
      // MERGE with existing pending unlocks (e.g., Profile Manager trophy from character flow)
      logger.debug('Backend Service - 💾 STEP 5: Merging and storing trophy unlocks to session storage...');
      if (trophyUnlocks.length > 0) {
        // Get existing unlocks and merge
        const existingStored = this.sessionService.get(this.PENDING_TROPHY_UNLOCKS_KEY);
        const existingUnlocks = existingStored ? JSON.parse(existingStored) : [];
        logger.debug('Backend Service - Found', existingUnlocks.length, 'existing pending trophy unlocks');

        // Combine existing and new unlocks
        const allUnlocks = [...existingUnlocks, ...trophyUnlocks];
        logger.debug('Backend Service - ✅ STORING', allUnlocks.length, 'TOTAL TROPHY UNLOCKS TO SESSION:', this.PENDING_TROPHY_UNLOCKS_KEY);
        logger.debug('Backend Service - Trophies being stored:', allUnlocks.map(t => ({
          type: t.trophyType,
          milestone: t.milestoneValue,
          value: t.value,
          isNew: t.isNewlyUnlocked
        })));
        this.sessionService.set(this.PENDING_TROPHY_UNLOCKS_KEY, JSON.stringify(allUnlocks));
        logger.debug('Backend Service - ✅ STORED SUCCESSFULLY');

        // Verify it was stored
        const stored = this.sessionService.get(this.PENDING_TROPHY_UNLOCKS_KEY);
        if (stored) {
          logger.debug('Backend Service - ✓ Verified: Trophy unlocks are in session storage');
        } else {
          logger.debug('Backend Service - ⚠️ WARNING: Failed to verify stored trophy unlocks!');
        }
      } else {
        logger.debug('Backend Service - ⚠️ NO TROPHY UNLOCKS TO STORE (length=0)');
      }

      // Persist all trophy data to ensure it's saved
      logger.debug('Backend Service - Persisting trophy data after story creation');
      await this.trophiesService.persistAllTrophyData();

      if (
        storyResponse.previousStoryId != null &&
        storyResponse.previousStoryId !== continuation.previousStoryId
      ) {
        logger.warn(
          'StoreesBackendService: server-echoed previousStoryId differs from request',
          {
            requestedPreviousStoryId: continuation.previousStoryId ?? null,
            serverPreviousStoryId: storyResponse.previousStoryId,
          },
        );
      }

      return {
        storyId: storyResponse.storyId,
        bucketEndpoint: storyResponse.bucketEndpoint,
        bucketName: storyResponse.bucketName,
        createdAt: storyResponse.createdAt,
        previousStoryId: storyResponse.previousStoryId ?? null,
        cyoaSegmentIndex: storyResponse.cyoaSegmentIndex ?? null,
      };
    } else {
      throw new Error('Failed to create story.');
    }
  }

  /** Response from `POST /v1/stories/import-shared-story`. */
  public async importSharedStory(
    jwtToken: string,
    profileId: string,
    sourceStoryId: string,
  ): Promise<{
    entryStoryId: string;
    importedStoryIds: string[];
    cyoaRootStoryId: string | null;
    syncRecommended: boolean;
    partialTreeSkipped: boolean;
  }> {
    const url = this.getStoreesBackendUrl(STORIES_IMPORT_SHARED_STORY_ROUTE);
    console.log('[IMPORT_SHARED_STORY] HTTP POST starting', {
      url,
      profileId,
      sourceStoryId,
    });
    logger.warn('[IMPORT_SHARED_STORY] HTTP POST starting', {
      url,
      profileIdSuffix: profileId.slice(-12),
      sourceStoryId,
    });
    const options = this.createPostOptionsWithJwtToken(
      STORIES_IMPORT_SHARED_STORY_ROUTE,
      jwtToken,
      { profileId, sourceStoryId },
    );
    const response = await CapacitorHttp.request(options);
    if (!response || response.status < 200 || response.status >= 300) {
      const data = response?.data as { error?: string; message?: string } | undefined;
      const serverError =
        data?.error || data?.message || 'Import shared story failed';
      console.error('[IMPORT_SHARED_STORY] HTTP POST failed', {
        status: response?.status ?? null,
        serverError,
        rawData: response?.data,
        profileId,
        sourceStoryId,
        url,
      });
      logger.warn('[IMPORT_SHARED_STORY] HTTP POST failed', {
        status: response?.status ?? null,
        serverError,
        profileIdSuffix: profileId.slice(-12),
        sourceStoryId,
        url,
      });
      logger.error(
        'StoreesBackendService: importSharedStory failed',
        JSON.stringify({
          status: response?.status ?? null,
          serverError,
          profileId,
          sourceStoryId,
        }),
      );
      throw new Error(serverError);
    }
    const body = response.data as {
      entryStoryId: string;
      importedStoryIds: string[];
      cyoaRootStoryId: string | null;
      syncRecommended: boolean;
      partialTreeSkipped: boolean;
    };
    console.log('[IMPORT_SHARED_STORY] HTTP POST success', {
      status: response.status,
      ...body,
      url,
    });
    logger.warn('[IMPORT_SHARED_STORY] HTTP POST success', {
      status: response.status,
      entryStoryId: body.entryStoryId,
      importedCount: body.importedStoryIds?.length,
      sourceStoryId,
      url,
    });
    return body;
  }

  /**
   * Get and clear pending trophy unlocks
   */
  public getPendingTrophyUnlocks(): TrophyUnlockNotification[] {
    logger.debug('Backend Service - getPendingTrophyUnlocks called');
    const stored = this.sessionService.get(this.PENDING_TROPHY_UNLOCKS_KEY);
    logger.debug('Backend Service - Stored value:', stored);

    if (!stored) {
      logger.debug('Backend Service - No stored trophy unlocks found');
      return [];
    }

    // Clear after retrieving
    this.sessionService.set(this.PENDING_TROPHY_UNLOCKS_KEY, '');
    logger.debug('Backend Service - Cleared pending trophy unlocks from storage');

    try {
      const parsed = JSON.parse(stored) as TrophyUnlockNotification[];
      logger.debug('Backend Service - Parsed trophy unlocks:', parsed);
      return parsed;
    } catch (error) {
      logger.error('Backend Service - Error parsing pending trophy unlocks:', error);
      return [];
    }
  }

  /**
   * Clear pending trophy unlocks without retrieving them
   * Used when navigating away from a screen that would display trophies
   */
  public clearPendingTrophyUnlocks(): void {
    logger.debug('Backend Service - clearPendingTrophyUnlocks called');
    this.sessionService.set(this.PENDING_TROPHY_UNLOCKS_KEY, '');
    logger.debug('Backend Service - Cleared pending trophy unlocks from storage');
  }

  public async createReport(payload: {
    target: 'image' | 'story';
    reason: string;
    comments?: string | null;
    storyId?: string | null;
    profileId?: string | null;
  }): Promise<unknown> {
    // Ensure we have a JWT token (anonymous or authenticated)
    let jwtToken = await this.sessionService.get(Constants.SESSION.JWT_TOKEN);
    if (!jwtToken) {
      jwtToken = await this.getAnonymousToken();
    }

    const options = this.createPostOptionsWithJwtToken(
      REPORTS_ROUTE,
      jwtToken,
      {
        target: payload.target,
        reason: payload.reason,
        comments: payload.comments ?? undefined,
        storyId: payload.storyId ?? undefined,
        profileId: payload.profileId ?? undefined,
      }
    );

    try {
      const response = await CapacitorHttp.request(<HttpOptions>options);
      return response.data;
    } catch (error) {
      logger.error('Error submitting report:', error);
      throw error;
    }
  }

  private getStoreesBackendUrl(route: string) {
    return urlJoin(apiBaseUrl, route);
  }

  private async getDeviceUniqueString(): Promise<string> {
    try {
      const deviceId = await Device.getId();

      if (deviceId.identifier) {
        return deviceId.identifier;
      }

      const storedFallback = await this.getPersistedAppSessionValue(this.DEVICE_UUID_KEY);
      if (storedFallback) {
        return storedFallback;
      }

      const generatedFallback = uuidv4();
      await this.persistAppSessionValue(this.DEVICE_UUID_KEY, generatedFallback);
      return generatedFallback;
    } catch (error) {
      logger.warn(
        'Failed to get device unique identifier, falling back to UUID:',
        error
      );

      const storedFallback = await this.getPersistedAppSessionValue(this.DEVICE_UUID_KEY);
      if (storedFallback) {
        return storedFallback;
      }

      const generatedFallback = uuidv4();
      await this.persistAppSessionValue(this.DEVICE_UUID_KEY, generatedFallback);
      return generatedFallback;
    }
  }

  private async getStableAnonymousId(): Promise<string> {
    const storedAnonymousId = await this.getPersistedAppSessionValue(
      this.STABLE_ANONYMOUS_ID_KEY
    );
    if (storedAnonymousId) {
      return storedAnonymousId;
    }

    const stableAnonymousId = uuidv5(
      await this.getDeviceUniqueString(),
      getStoreesNamespace()
    );
    await this.persistAppSessionValue(
      this.STABLE_ANONYMOUS_ID_KEY,
      stableAnonymousId
    );
    return stableAnonymousId;
  }

  private async getPersistedAppSessionValue(key: string): Promise<string | null> {
    const dexieValue = await this.dexieDb.getAppSessionValue(key);
    if (dexieValue) {
      return dexieValue;
    }

    const legacyValue = await this.sessionService.getPersistent(key);
    if (!legacyValue) {
      return null;
    }

    const normalizedValue =
      typeof legacyValue === 'string' ? legacyValue : String(legacyValue);
    await this.dexieDb.setAppSessionValue(key, normalizedValue);
    return normalizedValue;
  }

  private async persistAppSessionValue(key: string, value: string): Promise<void> {
    await this.dexieDb.setAppSessionValue(key, value);
    await this.sessionService.setPersistent(key, value);
  }

  public async getAnonymousToken(existingToken?: string): Promise<string> {
    if (this.anonymousTokenInFlight) {
      return this.anonymousTokenInFlight;
    }

    const url = this.getStoreesBackendUrl(GET_ANONYMOUS_TOKEN_ROUTE);
    this.anonymousTokenInFlight = this.performGetAnonymousToken(
      existingToken,
      url
    ).finally(() => {
      this.anonymousTokenInFlight = null;
    });

    return this.anonymousTokenInFlight;
  }

  private async performGetAnonymousToken(
    existingToken: string | undefined,
    url: string
  ): Promise<string> {
    let anonymousToken = '';
    let data: { currentId: string };
    if (existingToken) {
      const decodedToken = jwtDecode<JwtPayload>(existingToken);
      data = { currentId: decodedToken.sub };
      await this.persistAppSessionValue(
        this.STABLE_ANONYMOUS_ID_KEY,
        decodedToken.sub
      );
    } else {
      data = { currentId: await this.getStableAnonymousId() };
    }
    const options = this.createAnonymousPostOptions(
      GET_ANONYMOUS_TOKEN_ROUTE,
      data
    );
    try {
      const response = await promiseWithTimeout(
        CapacitorHttp.request(<HttpOptions>options),
        STOREES_BACKEND_HTTP_TIMEOUT_MS,
        'getAnonymousToken Http'
      );
      logger.debug('Response: ', response.data);
      if (response) {
        const anonymousTokenResponse = response.data as AnonymousTokenResponse;
        const { token, anonymousId } = anonymousTokenResponse;
        await this.persistAppSessionValue(
          this.STABLE_ANONYMOUS_ID_KEY,
          anonymousId
        );
        this.sessionService.set(Constants.SESSION.JWT_TOKEN, token);
        anonymousToken = token;
      }
    } catch (error) {
      logger.error('getAnonymousToken failed', { url, error });
      await this.showAlert(() => this.getAnonymousToken(existingToken));
    }

    return anonymousToken;
  }

  private createAnonymousPostOptions(route: string, data: unknown): unknown {
    return {
      url: this.getStoreesBackendUrl(route),
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': storeesBackendApiKey,
      },
      data,
    };
  }

  private async showAlert(getTokenFn: () => Promise<string>) {
    const alert = await this.alertCtrl.create({
      header: this.translate.instant('general.errors.accessDenied'),
      message: this.translate.instant('general.errors.connectionError'),
      buttons: [
        {
          text: this.translate.instant('general.ok'),
          handler: () => {
            return getTokenFn();
          },
        },
      ],
    });

    await alert.present();
  }

  public async upgradeUser(
    jwtToken: string,
    oryToken: string
  ): Promise<unknown> {
    const options = this.createUpgradeUserPostOptions(
      UPGRADE_USER_ROUTE,
      { dummy: 'dummy' },
      jwtToken,
      oryToken
    );
    const response = await CapacitorHttp.request(<HttpOptions>options);

    if (!response || response.status < 200 || response.status >= 300) {
      const errorMessage =
        response?.data?.error ||
        response?.data?.message ||
        `Upgrade user failed (HTTP ${response?.status || 'unknown'})`;
      logger.error('Upgrade user failed', {
        status: response?.status,
        data: response?.data,
      });
      throw new Error(errorMessage);
    }

    return response.data;
  }

  private createUpgradeUserPostOptions(
    route: string,
    data: unknown,
    jwtToken: string,
    oryToken: string
  ): unknown {
    return {
      url: this.getStoreesBackendUrl(route),
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + jwtToken,
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-ory-token': 'Bearer ' + oryToken,
      },
      data,
    };
  }

  public async appOpen(jwtToken: string): Promise<void> {
    const options = this.createPostOptionsWithJwtToken(
      APP_OPEN_ROUTE,
      jwtToken,
      { dummy: 'dummy' }
    );
    try {
      const response = await CapacitorHttp.request(options);
      if (!response || response.status < 200 || response.status >= 300) {
        logger.warn('StoreesBackendService: appOpen non-success', {
          status: response?.status,
        });
        return;
      }
    } catch (error) {
      logger.error('StoreesBackendService: appOpen failed', error);
    }
  }

  public async upsertDevicePushToken(
    jwtToken: string,
    payload: DevicePushRegistrationPayload
  ): Promise<void> {
    const options = this.createPutOptionsWithJwtToken(
      DEVICE_PUSH_ROUTE,
      jwtToken,
      payload
    );

    try {
      const response = await CapacitorHttp.request(options);
      if (!response || response.status < 200 || response.status >= 300) {
        logger.error('StoreesBackendService: device push upsert rejected', {
          status: response?.status,
          body: response?.data,
          hint:
            response?.status === 401
              ? 'JWT rejected — device-push body userId must be GET /v1/users/me id (stable user id), not UserIdentity.providerUserId.'
              : undefined,
        });
        throw new Error(
          `device push upsert failed (HTTP ${response?.status ?? 'unknown'})`
        );
      }
      logger.info('StoreesBackendService: device push token upserted', {
        platform: payload.platform,
        tokenPrefix: payload.token.slice(0, 12),
        status: response.status,
      });
    } catch (error) {
      logger.error('StoreesBackendService: Error syncing device push token', error);
      throw error;
    }
  }

  public async getDailyStory(
    jwtToken: string,
    campaignDate: string
  ): Promise<DailyStoryResponse> {
    const options = this.createGetOptionsWithJwtToken(
      `${DAILY_STORY_ROUTE}?campaignDate=${encodeURIComponent(
        campaignDate
      )}`,
      jwtToken
    ) as HttpOptions;

    const response = await CapacitorHttp.request(options);
    if (!response || response.status < 200 || response.status >= 300) {
      const errorMessage =
        response?.data?.message ||
        response?.data?.error ||
        'Failed to fetch daily story';
      throw new Error(
        `Failed to fetch daily story: ${errorMessage} (HTTP ${response?.status || 'unknown'})`
      );
    }

    return response.data as DailyStoryResponse;
  }

  public async postPushInteraction(
    jwtToken: string,
    payload: PushInteractionPayload
  ): Promise<void> {
    const options = this.createPostOptionsWithJwtToken(
      PUSH_INTERACTION_ROUTE,
      jwtToken,
      payload
    );
    await CapacitorHttp.request(options);
  }

  private createPostOptionsWithJwtToken(
    route: string,
    jwtToken: string,
    data?: unknown
  ): HttpOptions {
    const options: HttpOptions = {
      url: this.getStoreesBackendUrl(route),
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + jwtToken,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };

    if (data) {
      options.data = data;
    }

    return options;
  }

  private createGetOptionsWithJwtToken(route: string, jwtToken: string): HttpOptions {
    const options: HttpOptions = {
      url: this.getStoreesBackendUrl(route),
      method: 'GET',
      headers: {
        Authorization: 'Bearer ' + jwtToken,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };

    return options;
  }

  public async putFavoriteStory(
    jwtToken: string,
    storyId: string,
    profileId: string,
    favorite: boolean
  ): Promise<void> {
    const options = this.createPutOptionsWithJwtToken(
      `${STORIES_ROUTE}/${storyId}`,
      jwtToken,
      {
        profileId,
        favorite,
      }
    );

    try {
      const response = await CapacitorHttp.request(options);
      return response.data;
    } catch (error) {
      logger.error('Error putting favorite story:', error);
      throw error;
    }
  }

  public async deleteUser(jwtToken: string): Promise<void> {
    const options = this.createDeleteOptionsWithJwtToken(
      `${USERS_ROUTE}/me`,
      jwtToken,
      { dummy: 'dummy' }
    );

    try {
      await CapacitorHttp.request(options);
    } catch (error) {
      logger.error('Error deleting user:', error);
      throw error;
    }
  }

  public async getCurrentUser(jwtToken: string): Promise<CurrentUserResponse> {
    const options = this.createGetOptionsWithJwtToken(`${USERS_ROUTE}/me`, jwtToken);
    const response = await CapacitorHttp.request(options as HttpOptions);

    if (!response || response.status < 200 || response.status >= 300) {
      const errorMessage =
        response?.data?.message ||
        response?.data?.error ||
        'Failed to fetch current user';
      throw new Error(errorMessage);
    }

    const user = response.data as CurrentUserResponse;
    // Cache backend user ID for SSV ad reward verification
    if (user.id) {
      this.sessionService.set('storees_backend_user_id', user.id);
    }
    return user;
  }

  public async checkVoiceCloneNarrationAccess(
    jwtToken: string
  ): Promise<VoiceCloneTrialState> {
    const options = this.createGetOptionsWithJwtToken(
      VOICE_CLONE_NARRATION_ACCESS_ROUTE,
      jwtToken
    );
    const response = await CapacitorHttp.request(options as HttpOptions);
    if (!response || response.status < 200 || response.status >= 300) {
      const errorMessage =
        response?.data?.message ||
        response?.data?.error ||
        'Failed to fetch voice clone narration access';
      throw new Error(errorMessage);
    }
    return response.data as VoiceCloneTrialState;
  }

  public async startVoiceCloneTrial(jwtToken: string): Promise<VoiceCloneTrialState> {
    const options = this.createPostOptionsWithJwtToken(
      VOICE_CLONE_TRIAL_START_ROUTE,
      jwtToken,
      {}
    );
    const response = await CapacitorHttp.request(options as HttpOptions);
    if (!response || response.status < 200 || response.status >= 300) {
      const errorMessage =
        response?.data?.message ||
        response?.data?.error ||
        'Failed to start voice clone trial';
      throw new Error(errorMessage);
    }
    return response.data as VoiceCloneTrialState;
  }

  public async optOutVoiceCloneTrial(jwtToken: string): Promise<VoiceCloneTrialState> {
    const options = this.createPostOptionsWithJwtToken(
      VOICE_CLONE_TRIAL_OPT_OUT_ROUTE,
      jwtToken,
      {}
    );
    const response = await CapacitorHttp.request(options as HttpOptions);
    if (!response || response.status < 200 || response.status >= 300) {
      const errorMessage =
        response?.data?.message ||
        response?.data?.error ||
        'Failed to opt out of voice clone trial';
      throw new Error(errorMessage);
    }
    return response.data as VoiceCloneTrialState;
  }

  private createPutOptionsWithJwtToken(
    route: string,
    jwtToken: string,
    data?: unknown
  ): HttpOptions {
    const options: HttpOptions = {
      url: this.getStoreesBackendUrl(route),
      method: 'PUT',
      headers: {
        Authorization: 'Bearer ' + jwtToken,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };

    if (data) {
      options.data = data;
    }

    return options;
  }

  private createDeleteOptionsWithJwtToken(
    route: string,
    jwtToken: string,
    data?: unknown
  ): HttpOptions {
    return {
      url: this.getStoreesBackendUrl(route),
      method: 'DELETE',
      headers: {
        Authorization: 'Bearer ' + jwtToken,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      data: data,
    };
  }
}
